import { Flex, Stack as ChakraStack } from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Bleed, Button, Card, Header, Heading, Stack, Text } from '@main/complykit';
import { useCreateOrganizationMutation } from '@main/graphql/mutations/CreateOrganization.admin.generated';
import { useGetOrganizationsQuerySubscription } from '@main/graphql/subscriptions/GetOrganizationsSubscription';
import { toError } from '@main/shared/utils';
import {
  errorToast,
  successToast,
  Table,
  useDrawer,
  useTableFiltersQuery,
  useTableSearchQuery,
} from '@main/ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useOrganizationsTableColumns } from './use-org-table-columns';

const tableId = 'organizations';

export const ManageOrganizations = () => {
  const { t } = useTranslation();
  const drawer = useDrawer();

  const { columns, onRowClick } = useOrganizationsTableColumns();

  const [columnFilters, setColumnFilters] = useTableFiltersQuery({
    columns,
    searchParam: 'organizationsFilter',
  });
  const [globalFilter, setGlobalFilter] = useTableSearchQuery({ searchParam: 'search' });

  const { isLoading, data } = useGetOrganizationsQuerySubscription();
  const [createOrganization] = useCreateOrganizationMutation();

  const onCreate = async () => {
    try {
      const orgDrawer = await drawer.openLoading({ entity: 'organization' });
      const response = await createOrganization().unwrap();

      if (!response.insert_organizations_one?.id) {
        throw new Error('Could not get organization id from response');
      }

      orgDrawer.load({ entityId: response.insert_organizations_one.id });
      successToast(t('successMessages.createSucceeded', { entity: t('entities.organization') }));
    } catch (error) {
      errorToast(t('errorMessages.createFailed', { entity: t('entities.organization') }));
      datadogLogs.logger.error('Organization creation failed.', {}, toError(error));
    }
  };
  const tableItemName = useMemo(() => {
    return {
      singular: 'organization',
      plural: 'organizations',
    };
  }, []);

  const organizations = data?.organizations || [];

  return (
    <ChakraStack spacing={6}>
      <Heading size="xl">Organizations</Heading>
      <Card>
        <Header isDivided>
          <Flex align="center" justify="space-between" gap={4} w="full">
            <Stack gap={0}>
              <Heading size="md">Organizations</Heading>
              <Text size="sm" color="muted">
                All organizations currently active on Complyance
              </Text>
            </Stack>

            <Button iconStart={PlusIcon} onPress={() => void onCreate()}>
              New organization
            </Button>
          </Flex>
        </Header>

        <Bleed>
          <Table
            id={tableId}
            entity="organization"
            data={organizations}
            isLoading={isLoading}
            columns={columns}
            itemName={tableItemName}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
            globalFilter={globalFilter}
            onGlobalFilterChange={setGlobalFilter}
            onRowClick={onRowClick}
          />
        </Bleed>
      </Card>
    </ChakraStack>
  );
};
